import { useAuth } from "@clerk/react"
import { useCallback, useEffect, useRef, useState } from "react"
import { useIdbState } from "../hooks/useIdbState"
import { useStoredState } from "../hooks/useStoredState"
import { useI18n } from "../i18n/useI18n"
import { markRecordSyncPending } from "../library/syncState"
import { SKILL_MAP } from "../match/engine/content/skills"
import {
  computeSkillCostBreakdown,
  computeStatCost,
} from "../match/engine/costs"
import { MAX_DERIVED_COST, validateCard } from "../match/engine/validator/cards"
import {
  ARTWORK_ASSET_STORAGE_KEY,
  ARTWORK_ASSET_STORAGE_VERSION,
  ArtworkAssetStoreSchema,
  collectArtworkAssets,
  EMPTY_ARTWORK_ASSET_STORE,
  upsertArtworkAssets,
} from "../media/assets"
import { getArtworkErrorLabel } from "../media/errors"
import { processArtworkFile } from "../media/processArtwork"
import { useArtworkSyncMutation } from "../media/query"
import { requestTurnstileToken } from "../media/turnstile"
import {
  ARTWORK_UPLOAD_STORAGE_KEY,
  ARTWORK_UPLOAD_STORAGE_VERSION,
  ArtworkUploadStoreSchema,
  createArtworkUploadRecord,
  EMPTY_ARTWORK_UPLOAD_STORE,
  updateArtworkUploadStatus,
  upsertArtworkUpload,
} from "../media/uploads"
import {
  createModerationIncident,
  EMPTY_MODERATION_INCIDENT_STORE,
  MODERATION_INCIDENT_STORAGE_KEY,
  MODERATION_INCIDENT_STORAGE_VERSION,
  ModerationIncidentStoreSchema,
} from "../moderation/storage"
import { validateModeratedText } from "../moderation/text"
import type {
  AuthoredCard,
  CardArtwork,
  CardColor,
  ManagedCardRecord,
  SkillDefinition,
  SkillSelection,
} from "../schemas"
import { ArtworkDropTarget, ArtworkOverlayControls } from "./ArtworkControls"
import type { CardEditingCallbacks } from "./CardFace"
import { CardFace } from "./CardFace"
import { PortalTargetProvider } from "./PortalTarget"
import { createCardId } from "./storage"
import { getCardValidationIssueLabel } from "./validationMessages"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Props = {
  open: boolean
  onClose: () => void
  editingRecord: ManagedCardRecord | null
  onSave: (record: ManagedCardRecord) => void
}

type FormValues = {
  name: string
  primaryColor: CardColor
  secondaryColor: CardColor | ""
  artwork?: CardArtwork
  attack: string
  health: string
  skills: SkillSelection[]
}

type ArtworkSyncStatus = "localOnly" | "retrying" | "synced" | "syncing" | null

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function computeDerivedCost(values: FormValues): {
  statCost: number
  skillCostDelta: number
  nonExhaustSurcharge: number
  selfDestructSurcharge: number
  secondaryColorSurcharge: 0 | 0.5
  derivedCost: number
} {
  const attack = Math.max(0, parseInt(values.attack, 10) || 0)
  const health = Math.max(1, parseInt(values.health, 10) || 1)
  const secondaryColorSurcharge: 0 | 0.5 =
    values.secondaryColor && values.primaryColor !== "colorless" ? 0.5 : 0
  const statCost = computeStatCost(attack, health)
  const skillCosts = computeSkillCostBreakdown(
    values.skills,
    SKILL_MAP,
    statCost + secondaryColorSurcharge,
  )
  const derivedCost = Math.ceil(
    statCost +
      skillCosts.skillCostDelta +
      skillCosts.nonExhaustSurcharge +
      skillCosts.selfDestructSurcharge +
      secondaryColorSurcharge,
  )
  return {
    statCost,
    skillCostDelta: skillCosts.skillCostDelta,
    nonExhaustSurcharge: skillCosts.nonExhaustSurcharge,
    selfDestructSurcharge: skillCosts.selfDestructSurcharge,
    secondaryColorSurcharge,
    derivedCost,
  }
}

function defaultValues(editing: ManagedCardRecord | null): FormValues {
  if (!editing) {
    return {
      name: "",
      primaryColor: "colorless",
      secondaryColor: "",
      artwork: undefined,
      attack: "2",
      health: "2",
      skills: [],
    }
  }
  return {
    name: editing.card.name,
    primaryColor: editing.card.primaryColor,
    secondaryColor: editing.card.secondaryColor ?? "",
    artwork: editing.card.artwork,
    attack: String(editing.card.stats.attack),
    health: String(editing.card.stats.health),
    skills: editing.card.selectedSkills,
  }
}

function getArtworkSyncStatusLabel(
  t: ReturnType<typeof useI18n>["t"],
  status: Exclude<ArtworkSyncStatus, null>,
) {
  switch (status) {
    case "localOnly":
      return t("ui.artwork.sync.localOnly")
    case "retrying":
      return t("ui.artwork.sync.retrying")
    case "synced":
      return t("ui.artwork.sync.synced")
    case "syncing":
      return t("ui.artwork.sync.syncing")
  }
}

function filterSkillsForColors(
  skills: SkillSelection[],
  primary: CardColor,
  secondary: CardColor | "",
): SkillSelection[] {
  if (primary === "colorless" || !secondary) return skills
  return skills.filter((sel) => {
    const def = SKILL_MAP.get(sel.skillId)
    if (!def) return false
    if (def.isColorless) return true
    return def.affinity === primary || def.affinity === secondary
  })
}

function applyAutoColors(
  values: FormValues,
  skills: SkillSelection[],
): FormValues {
  const next = { ...values, skills }
  const affinities = [
    ...new Set(
      skills
        .map((s) => SKILL_MAP.get(s.skillId))
        .filter((d): d is SkillDefinition => !!d && !d.isColorless)
        .map((d) => d.affinity as CardColor),
    ),
  ]

  if (affinities.length === 0) return next

  const [firstAffinity] = affinities
  if (next.primaryColor === "colorless" && firstAffinity) {
    next.primaryColor = firstAffinity
  }

  if (!next.secondaryColor) {
    const secondaryAffinity = affinities.find((a) => a !== next.primaryColor)
    if (secondaryAffinity) next.secondaryColor = secondaryAffinity
  }

  return next
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CardEditorModal({
  open,
  onClose,
  editingRecord,
  onSave,
}: Props) {
  const { t } = useI18n()
  const { userId } = useAuth()
  const artworkSyncMutation = useArtworkSyncMutation()
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [dialogEl, setDialogEl] = useState<HTMLDialogElement | null>(null)
  const handleDialogRef = useCallback((el: HTMLDialogElement | null) => {
    dialogRef.current = el
    setDialogEl(el)
  }, [])
  const [values, setValues] = useState<FormValues>(() =>
    defaultValues(editingRecord),
  )
  const [errors, setErrors] = useState<string[]>([])
  const [artworkError, setArtworkError] = useState<string | null>(null)
  const [artworkBusy, setArtworkBusy] = useState(false)
  const [artworkSyncBusy, setArtworkSyncBusy] = useState(false)
  const [artworkSyncStatus, setArtworkSyncStatus] =
    useState<ArtworkSyncStatus>(null)
  const [, setModerationIncidents] = useStoredState(
    MODERATION_INCIDENT_STORAGE_KEY,
    ModerationIncidentStoreSchema,
    {
      defaultValue: EMPTY_MODERATION_INCIDENT_STORE,
      version: MODERATION_INCIDENT_STORAGE_VERSION,
    },
  )
  const [, setArtworkUploads] = useStoredState(
    ARTWORK_UPLOAD_STORAGE_KEY,
    ArtworkUploadStoreSchema,
    {
      defaultValue: EMPTY_ARTWORK_UPLOAD_STORE,
      version: ARTWORK_UPLOAD_STORAGE_VERSION,
    },
  )
  const [artworkAssets, , setArtworkAssets] = useIdbState(
    ARTWORK_ASSET_STORAGE_KEY,
    ArtworkAssetStoreSchema,
    {
      defaultValue: EMPTY_ARTWORK_ASSET_STORE,
      version: ARTWORK_ASSET_STORAGE_VERSION,
    },
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on reopen
  useEffect(() => {
    setValues(defaultValues(editingRecord))
    setErrors([])
    setArtworkError(null)
    setArtworkBusy(false)
    setArtworkSyncBusy(false)
    setArtworkSyncStatus(null)
  }, [editingRecord, open])

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    else if (!open && dialog.open) dialog.close()
  }, [open])

  const handleSkillsChange = (skills: SkillSelection[]) => {
    setValues((prev) => applyAutoColors(prev, skills))
  }

  const {
    statCost,
    skillCostDelta,
    nonExhaustSurcharge,
    selfDestructSurcharge,
    secondaryColorSurcharge,
    derivedCost,
  } = computeDerivedCost(values)

  const attack = Math.max(0, parseInt(values.attack, 10) || 0)
  const health = Math.max(1, parseInt(values.health, 10) || 1)
  const exceedsMaxCost = derivedCost > MAX_DERIVED_COST
  const canSave = !exceedsMaxCost

  const syncArtworkEnvelope = async (artwork: CardArtwork) => {
    if (!userId) {
      throw new Error(t("ui.artwork.error.syncUnavailable"))
    }

    const assets = collectArtworkAssets(artwork, artworkAssets)
    if (assets.length === 0) {
      throw new Error(t("ui.artwork.error.noLocalVariants"))
    }

    const turnstileToken = await requestTurnstileToken()
    return artworkSyncMutation.mutateAsync({
      payload: { artwork, assets },
      turnstileToken,
    })
  }

  const handleArtworkFile = async (file: File | null) => {
    if (!file) return

    const uploadRecord = createArtworkUploadRecord({
      originalFilename: file.name,
      mimeType: file.type || "application/octet-stream",
      byteLength: file.size,
    })

    setArtworkUploads((previous) => upsertArtworkUpload(previous, uploadRecord))
    setArtworkBusy(true)
    setArtworkError(null)
    setArtworkSyncStatus(null)

    try {
      const processed = await processArtworkFile(file)
      setArtworkAssets((previous) =>
        upsertArtworkAssets(previous, processed.assets),
      )
      let syncedArtwork = processed.artwork
      try {
        setArtworkSyncBusy(true)
        setArtworkSyncStatus("syncing")
        if (!userId) {
          throw new Error(t("ui.artwork.error.syncUnavailable"))
        }
        const turnstileToken = await requestTurnstileToken()
        const synced = await artworkSyncMutation.mutateAsync({
          payload: processed,
          turnstileToken,
        })
        syncedArtwork = synced.artwork
        setArtworkSyncStatus("synced")
        setArtworkError(null)
      } catch (error) {
        // Keep the local asset cache as a fallback when the worker media path
        // is unavailable in development or not yet configured.
        const message =
          getArtworkErrorLabel(t, error) ?? t("ui.artwork.error.uploadFailed")
        setArtworkError(message)
        setArtworkSyncStatus("localOnly")
      } finally {
        setArtworkSyncBusy(false)
      }
      setArtworkUploads((previous) =>
        updateArtworkUploadStatus(previous, uploadRecord.uploadId, "ready", {
          hash: syncedArtwork.hash,
          variants: syncedArtwork.variants,
        }),
      )
      setValues((previous) => ({
        ...previous,
        artwork: syncedArtwork,
      }))
    } catch (error) {
      const message =
        getArtworkErrorLabel(t, error) ?? t("ui.artwork.error.uploadFailed")
      setArtworkError(message)
      setArtworkUploads((previous) =>
        updateArtworkUploadStatus(previous, uploadRecord.uploadId, "rejected", {
          rejectionReason: message,
        }),
      )
    } finally {
      setArtworkBusy(false)
    }
  }

  const handleRetryArtworkSync = async () => {
    if (!values.artwork) return

    setArtworkSyncBusy(true)
    setArtworkSyncStatus("retrying")
    setArtworkError(null)
    try {
      const synced = await syncArtworkEnvelope(values.artwork)
      setValues((previous) => ({
        ...previous,
        artwork: synced.artwork,
      }))
      setArtworkSyncStatus("synced")
    } catch (error) {
      const message =
        getArtworkErrorLabel(t, error) ?? t("ui.artwork.error.uploadFailed")
      setArtworkError(message)
      setArtworkSyncStatus("localOnly")
    } finally {
      setArtworkSyncBusy(false)
    }
  }

  const handleSave = () => {
    const errs: string[] = []
    if (!values.name.trim()) errs.push(t("cardEditor.error.nameRequired"))
    const moderatedNameError = validateModeratedText(
      values.name,
      t("cardEditor.moderationLabel"),
      (label) => t("moderation.error.blockedLanguage", { label }),
    )
    if (moderatedNameError) {
      errs.push(moderatedNameError)
      setModerationIncidents((previous) => [
        createModerationIncident("card_name", values.name),
        ...previous,
      ])
    }
    if (
      Number.isNaN(parseInt(values.attack, 10)) ||
      parseInt(values.attack, 10) < 0
    )
      errs.push(t("cardEditor.error.attackMin"))
    if (
      Number.isNaN(parseInt(values.health, 10)) ||
      parseInt(values.health, 10) < 1
    )
      errs.push(t("cardEditor.error.healthMin"))

    if (errs.length > 0) {
      setErrors(errs)
      return
    }

    const card: AuthoredCard = {
      id: editingRecord?.card.id ?? createCardId(values.name.trim()),
      kind: "authored_unit",
      name: values.name.trim(),
      primaryColor: values.primaryColor,
      secondaryColor: values.secondaryColor || undefined,
      artwork: values.artwork,
      stats: { attack, health },
      selectedSkills: values.skills,
      authoredCostInputs: {
        skillCostDelta,
        nonExhaustSurcharge,
        selfDestructSurcharge,
        secondaryColorSurcharge,
      },
      derivedCost,
    }

    const validation = validateCard(card)
    const validationErrors = validation.issues
      .filter((i) => i.severity === "error")
      .map((i) => getCardValidationIssueLabel(t, i))

    if (validationErrors.length > 0) {
      setErrors(validationErrors)
      return
    }

    const record: ManagedCardRecord = {
      card,
      ownerUserId: userId ?? editingRecord?.ownerUserId,
      remoteSyncFailedAt: undefined,
      remoteSyncedAt: undefined,
      updatedAt: new Date().toISOString(),
      deckUsageCount: editingRecord?.deckUsageCount ?? 0,
    }

    setErrors([])
    onSave(markRecordSyncPending(record))
  }

  const editing: CardEditingCallbacks = {
    onNameChange: (name) => setValues((prev) => ({ ...prev, name })),
    onPrimaryColorChange: (primaryColor) => {
      setValues((prev) => ({
        ...prev,
        primaryColor,
        skills: filterSkillsForColors(
          prev.skills,
          primaryColor,
          prev.secondaryColor,
        ),
      }))
    },
    onSecondaryColorChange: (color) => {
      setValues((prev) => ({
        ...prev,
        secondaryColor: color ?? "",
        skills: filterSkillsForColors(
          prev.skills,
          prev.primaryColor,
          color ?? "",
        ),
      }))
    },
    onAttackChange: (attack) =>
      setValues((prev) => ({ ...prev, attack: String(attack) })),
    onHealthChange: (health) =>
      setValues((prev) => ({ ...prev, health: String(health) })),
    onSkillsChange: handleSkillsChange,
  }

  return (
    <dialog
      className="card-editor-dialog card-editor-dialog-centered"
      onCancel={(e) => {
        e.preventDefault()
        onClose()
      }}
      ref={handleDialogRef}
    >
      <PortalTargetProvider value={dialogEl}>
        <div className="card-editor-inner">
          <div className="card-editor-header">
            <h2>
              {editingRecord
                ? t("cardEditor.editTitle")
                : t("cardEditor.createTitle")}
            </h2>
            <button
              aria-label={t("common.close")}
              className="card-editor-close"
              onClick={onClose}
              type="button"
            >
              ✕
            </button>
          </div>

          <form
            className="card-editor-form"
            onSubmit={(e) => {
              e.preventDefault()
              handleSave()
            }}
          >
            <div className="card-editor-body">
              <CardFace
                artwork={values.artwork}
                artworkAssets={artworkAssets}
                name={values.name}
                primaryColor={values.primaryColor}
                secondaryColor={values.secondaryColor || undefined}
                stats={{ attack, health }}
                cost={derivedCost}
                costIsError={exceedsMaxCost}
                costBreakdown={{
                  stats: statCost,
                  skillDelta:
                    skillCostDelta +
                    nonExhaustSurcharge +
                    selfDestructSurcharge,
                  secondaryColorSurcharge,
                }}
                skills={values.skills}
                size="lg"
                editing={editing}
                artworkEditorOverlay={
                  <>
                    <ArtworkDropTarget
                      addLabel={t("ui.artwork.addImage")}
                      artwork={values.artwork}
                      busy={artworkBusy}
                      onSelectFile={(file) => void handleArtworkFile(file)}
                      replaceLabel={t("ui.artwork.replaceImage")}
                    />
                    <ArtworkOverlayControls
                      error={artworkError}
                      onRemove={
                        values.artwork
                          ? () =>
                              setValues((previous) => ({
                                ...previous,
                                artwork: undefined,
                              }))
                          : undefined
                      }
                      onRetrySync={
                        values.artwork && artworkSyncStatus === "localOnly"
                          ? () => void handleRetryArtworkSync()
                          : undefined
                      }
                      removeLabel={t("ui.artwork.removeImage")}
                      retrySyncBusy={artworkSyncBusy}
                      retrySyncLabel={t("ui.artwork.retrySync")}
                      retryingLabel={t("ui.artwork.retrying")}
                      syncStatusLabel={
                        artworkSyncStatus && artworkSyncStatus !== "synced"
                          ? getArtworkSyncStatusLabel(t, artworkSyncStatus)
                          : null
                      }
                    />
                  </>
                }
                style={{ width: "min(20rem, 80vw)" }}
              />
            </div>

            {errors.length > 0 && (
              <ul className="form-errors">
                {errors.map((e) => (
                  <li className="field-error" key={e}>
                    {e}
                  </li>
                ))}
              </ul>
            )}
            {exceedsMaxCost && (
              <p className="field-error">
                {t("cardEditor.error.maxCost", {
                  cost: derivedCost,
                  max: MAX_DERIVED_COST,
                })}
              </p>
            )}

            <div className="card-editor-actions">
              <button
                className="card-editor-action-button card-editor-action-button-secondary"
                onClick={onClose}
                type="button"
              >
                {t("common.cancel")}
              </button>
              <button
                className="card-editor-action-button card-editor-action-button-primary"
                disabled={!canSave}
                title={
                  exceedsMaxCost
                    ? t("cardEditor.error.maxCostTitle", {
                        max: MAX_DERIVED_COST,
                      })
                    : undefined
                }
                type="submit"
              >
                {editingRecord
                  ? t("common.saveChanges")
                  : t("cardEditor.createTitle")}
              </button>
            </div>
          </form>
        </div>
      </PortalTargetProvider>
    </dialog>
  )
}
